import React, { useEffect, useState } from "react";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import {
  setSellerAccessType,
  setSellerLoginDate,
  setSellerTeams,
  setSellerTokens,
  setSellerUser,
} from "store";
import { Button } from "../../../shadcn/components/ui/button";
import { Input } from "../../../shadcn/components/ui/input";
import { Label } from "../../../shadcn/components/ui/label";
import Logo from "../../../icons/Logo";
import SellerLoginHero from "./SellerLoginHero";
import OtpInput from "./OtpInput";
import SpinLoader from "../../buyer/SpinLoader";
import {
  getSellerTeamMembers,
  getUser,
  intiateLoginWithOtp as initiateOtpLogin,
  loginWithOtp,
  verifyTeamMemberRequest,
} from "services";

export default function Login() {
  const router = useRouter();
  const dispatch = useDispatch();
  const { accessToken } = useSelector((state: any) => state.sellerAuth);
  const [isRouteGuardLoading, setIsRouteGuardLoading] = useState(true);
  const [step, setStep] = useState<"email" | "otp">("email");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const form = useBetaForm({
    email: "",
    otp: "",
  });
  const isEmailStep = step === "email";

  const formConstraints = {
    email: {
      presence: {
        allowEmpty: false,
        message: "^Email is required.",
      },
      email: {
        message: "^Please enter a valid email address.",
      },
    },
    otp: {
      presence: {
        allowEmpty: false,
        message: "^OTP is required.",
      },
      length: {
        is: 6,
        message: "^OTP must be 6 digits.",
      },
      format: {
        pattern: "^[0-9]{6}$",
        flags: "i",
        message: "^OTP must contain only digits.",
      },
    },
  };

  useEffect(() => {
    if (!router.isReady) return;

    if (accessToken) {
      router.replace("/");
      return;
    }

    setIsRouteGuardLoading(false);
  }, [accessToken, router, router.isReady]);

  useEffect(() => {
    if (step === "email") {
      form.setValidationRules({ email: formConstraints.email });
    } else {
      form.setValidationRules({ otp: formConstraints.otp });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step !== "otp" || resendCooldownSeconds <= 0) {
      return;
    }
    const timer = setInterval(() => {
      setResendCooldownSeconds((previousSeconds) => {
        if (previousSeconds <= 1) {
          clearInterval(timer);
          return 0;
        }
        return previousSeconds - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step, resendCooldownSeconds]);

  const handleEmailSubmission = async () => {
    if (!form.validate()) {
      return;
    }
    try {
      setIsSendingOtp(true);
      const normalizedEmail = String(form.getField("email") || "")
        .trim()
        .toLowerCase();
      form.setField("email", normalizedEmail);
      await initiateOtpLogin(form);
      form.setField("otp", "");
      setResendCooldownSeconds(30);
      setStep("otp");
    } catch (error) {
      console.log("Error while initiating OTP login", error);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (isResendingOtp || resendCooldownSeconds > 0) {
      return;
    }
    try {
      setIsResendingOtp(true);
      const normalizedEmail = String(form.getField("email") || "")
        .trim()
        .toLowerCase();
      form.setField("email", normalizedEmail);
      await initiateOtpLogin(form);
      setResendCooldownSeconds(30);
    } catch (error) {
      console.log("Error in resend code", error);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!form.validate()) {
      console.log("form is not valid");
      return;
    }
    try {
      setIsVerifyingOtp(true);
      const data: any = await loginWithOtp(form);
      dispatch(setSellerTokens(data));
      const user: any = await getUser();
      const teams: any = await verifyTeamMemberRequest({
        user_uuid: user?.uuid,
      });
      // const teams = {
      //   valid: true,
      //   teams: [
      //     {
      //       uuid: "edd7347d-5d10-4ede-9164-d838e1d5c414",
      //       creator_user_uuid: null,
      //       name: "1553a439-f2dd-4605-9340-ddfbac903b30",
      //       slug: "1553a439-f2dd-4605-9340-ddfbac903b30",
      //       tenant: "swiss-asset",
      //       meta: {
      //         name: "1553a439-f2dd-4605-9340-ddfbac903b30",
      //         type: "swiss-asset",
      //         asset_data: {
      //           uuid: "1553a439-f2dd-4605-9340-ddfbac903b30",
      //           status: "draft",
      //           dpd_days: 92,
      //           npa_date: "2025-11-15T00:00:00.000Z",
      //           created_at: "2026-04-13T13:17:04.460Z",
      //           created_by: "admin",
      //           deleted_at: null,
      //           listing_id: "ABSW-26-000001",
      //           updated_at: "2026-04-13T13:17:04.460Z",
      //           legal_status: "notice_issued",
      //           borrower_name: "Ravi Kumar",
      //           security_type: "residential_property",
      //           valuation_date: "2026-03-20T00:00:00.000Z",
      //           sanction_amount: "25000000",
      //           data_cutoff_date: "2026-03-31T00:00:00.000Z",
      //           valuation_amount: "22000000.50",
      //           litigation_status: "none",
      //           portfolio_segment: "retail_mortgage",
      //           total_outstanding: "19875000",
      //           number_of_accounts: 1,
      //           geographical_spread: "NCR",
      //           loan_account_number: "LAN-2026-000145",
      //           borrower_entity_type: "individual",
      //           aggregate_outstanding: "19875000.75",
      //           outstanding_principal: "18250000",
      //           secured_asset_location: "Sector 62, Noida, Uttar Pradesh",
      //           asset_transfer_event_id: null,
      //           secured_asset_description: "3BHK apartment with parking",
      //         },
      //         bashBoardUrl: "https://sat.quickbid.in/",
      //       },
      //       created_at: "2026-04-13T13:17:05.983Z",
      //       updated_at: "2026-04-13T13:17:05.983Z",
      //       membership_uuid: "6c8133f6-470e-4563-8bb3-327cb5f03e14",
      //       membership_permissions: {
      //         role: "approver",
      //       },
      //     },
      //   ],
      // };
      // const teams = {
      //   valid: false,
      //   teams: [],
      // };
      if (teams?.valid && teams?.teams?.length > 0) {
        dispatch(setSellerUser(user));
        dispatch(setSellerTeams(teams.teams));
        dispatch(
          setSellerAccessType({
            access_type: "seller",
          }),
        );
        const loginDateIST = DateTime.now()
          .setZone("Asia/Kolkata")
          .toFormat("yyyy-MM-dd");
        dispatch(setSellerLoginDate({ loginDate: loginDateIST }));
        await router.replace("/");
      }
    } catch (error: any) {
      console.log("check here error", error);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  if (isRouteGuardLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#F8F8F8]">
        <SpinLoader />
      </div>
    );
  }

  return (
    <div className="w-full dark:bg-[#152e4d]">
      <div className="w-full min-h-screen flex flex-col 2xl:w-[1340px] 2xl:mx-auto 2xl:px-0">
        <header className="w-full flex items-center justify-between px-customPaddingMobile lg:px-customPadding py-3 md:py-4 border-b border-brand-borderGreyTwo">
          <Logo />
          <span className="text-lg font-medium capitalize tracking-wider text-brand-orange">
            Seller App
          </span>
        </header>

        <main className="flex-1 flex items-center justify-center px-customPaddingMobile lg:px-customPadding">
          <div className="w-full flex flex-col justify-center items-center lg:items-stretch md:flex-row px-0 md:px-4 gap-6 md:gap-8">
            <SellerLoginHero />

            <div className="w-full lg:w-1/2 flex justify-center">
              <div className="w-full bg-[#F8F8F8] px-4 py-8 lg:py-10 lg:px-10 clip-path-bottomRightFooterMobile lg:clip-path-bottomRightPropertyCountCard rounded-xl shadow-sm">
                {isEmailStep ? (
                  <div className="pb-12">
                    <h2 className="text-2xl lg:text-3xl text-brand-textBlack">
                      Get Started
                    </h2>
                    <p className="text-xs lg:text-sm mt-2 text-gray-600">
                      Enter your email to access your account
                    </p>
                    <div className="mt-6 space-y-3">
                      <Label
                        htmlFor="seller-login-email"
                        className="block text-xs lg:text-sm text-gray-700 mb-1"
                      >
                        Email address
                      </Label>
                      <Input
                        id="seller-login-email"
                        type="email"
                        placeholder="Enter your email"
                        value={form.getField("email")}
                        onChange={(e) => form.setField("email", e.target.value)}
                        autoComplete="email"
                        className="w-full h-10 lg:h-12 px-3 lg:px-4 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-orange font-openSans text-sm"
                      />
                      {form.errors.get("email") && (
                        <p className="text-xs text-red-700">
                          {form.errors.get("email")[0]}
                        </p>
                      )}
                    </div>
                    <Button
                      className="w-full mt-6 lg:mt-10"
                      onClick={handleEmailSubmission}
                      disabled={isSendingOtp}
                    >
                      {isSendingOtp ? "Sending OTP..." : "Get OTP"}
                    </Button>
                    <p className="mt-4 text-[11px] lg:text-xs text-gray-500 text-center leading-snug">
                      By continuing, you agree to the{" "}
                      <span className="underline">terms &amp; conditions</span>{" "}
                      and <span className="underline">privacy policy</span>.
                    </p>
                  </div>
                ) : (
                  <div className="pb-12">
                    <h2 className="text-2xl lg:text-3xl text-brand-textBlack">
                      Verify OTP
                    </h2>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-3">
                      <p className="text-xs lg:text-sm text-gray-600">
                        OTP sent to{" "}
                        <span className="font-semibold">
                          {form.getField("email") || "your email"}
                        </span>
                      </p>
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-xs lg:text-sm underline text-login-color self-start"
                        onClick={() => {
                          setStep("email");
                        }}
                      >
                        Change
                      </Button>
                    </div>
                    <div className="mt-6">
                      <p className="text-xs lg:text-sm font-medium mb-3">
                        Enter 6-digit OTP
                      </p>
                      <OtpInput
                        otp={form.getField("otp")}
                        onChange={(value) => form.setField("otp", value)}
                      />
                      {form.errors.get("otp") && (
                        <p className="mt-2 text-xs text-red-700">
                          {form.errors.get("otp")[0]}
                        </p>
                      )}
                      <p className="mt-3 text-[11px] lg:text-xs text-gray-500">
                        Didn&apos;t receive the OTP?{" "}
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto p-0 underline text-login-color text-[11px] lg:text-xs"
                          onClick={handleResendOtp}
                          disabled={isResendingOtp || resendCooldownSeconds > 0}
                        >
                          {isResendingOtp
                            ? "Resending..."
                            : resendCooldownSeconds > 0
                            ? `Resend OTP in ${resendCooldownSeconds}s`
                            : "Resend OTP"}
                        </Button>
                      </p>
                    </div>
                    <Button
                      className="w-full mt-8 lg:mt-6"
                      onClick={handleVerifyOtp}
                      disabled={isVerifyingOtp}
                    >
                      {isVerifyingOtp ? "Verifying..." : "Verify"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
